import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type ReactNode,
} from 'react';

import {
  trackInteraction,
  trackPageView,
} from 'src/modules/analytics/analytics';
import type { AnalyticsEvents } from 'src/modules/analytics/analyticsEvents';
import { usePage } from 'src/provider/page';

import { useAuthStatus } from './auth';

interface State {
  event?: AnalyticsEvents;
  analyticsPayload?: object;
}

type Action =
  | {
      type: 'TRACK_PAGE_VIEW';
    }
  | {
      type: 'TRACK_INTERACTION';
      payload: {
        event: AnalyticsEvents;
        analyticsPayload?: object;
      };
    };

interface AnalyticsContextType {
  state: State;
  dispatch: React.Dispatch<Action>;
  trackAnalyticsInteraction: (
    event: AnalyticsEvents,
    analyticsPayload?: object,
  ) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

const analyticsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TRACK_PAGE_VIEW':
      return {
        ...state,
      };
    case 'TRACK_INTERACTION':
      return {
        ...state,
        event: action.payload.event,
        analyticsPayload: action.payload.analyticsPayload,
      };
    default:
      throw new Error('Unknown action type');
  }
};

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const useTrackPageView = (categoryName?: string, threadId?: string) => {
  const { authStatus } = useAuthStatus();
  const {
    pageState: { pageName, pageNumber },
  } = usePage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [state, dispatch] = useReducer(analyticsReducer, {
    analyticsPayload: undefined,
  });
  useEffect(() => {
    if (pageName !== 'unknown' && authStatus !== 'pending') {
      dispatch({
        type: 'TRACK_PAGE_VIEW',
      });
      if (
        pageName === 'CommunityAllGroups' ||
        pageName === 'CommunityMyGroups' ||
        pageName === 'CommunityTopGroups'
      ) {
        trackPageView(pageName, { pageNumber });
      } else if (pageName === 'CommunityGroupDetails') {
        trackPageView(pageName, { learningCategory: categoryName });
      } else if (pageName === 'CommunityThreadDetails') {
        trackPageView(pageName, { threadId, learningCategory: categoryName });
      } else {
        trackPageView(pageName);
      }
    }
  }, [authStatus, pageName, dispatch, pageNumber, categoryName, threadId]);
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const {
    pageState: { pageName },
  } = usePage();
  const [state, dispatch] = useReducer(analyticsReducer, {
    analyticsPayload: undefined,
  });

  const trackAnalyticsInteraction = (
    event: AnalyticsEvents,
    analyticsPayload?: object,
  ): void => {
    if (!event) return;

    dispatch({
      type: 'TRACK_INTERACTION',
      payload: { event, analyticsPayload },
    });

    trackInteraction(pageName, event, analyticsPayload);
  };
  return (
    <AnalyticsContext.Provider
      value={{
        state,
        dispatch,
        trackAnalyticsInteraction,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within a AnalyticsProvider');
  }
  return context;
};
